import React from "react";
import {
  ClipboardList,
  Clock3,
  FileBadge2,
  Home,
  Menu,
  Users,
} from "lucide-react";

/**
 * MobileBottomNav: Native Play Store style bottom navigation bar.
 * Appears only on mobile/tablet viewports (<= 768px).
 * Gives instant native app feel with tactile feedback, active pill glows,
 * and quick access to core workflows + slide-out menu drawer.
 */
const MobileBottomNav = ({
  currentSection,
  onNavigate,
  onOpenDrawer,
  userRole = "Employee",
  pendingBadgeCount = 0,
}) => {
  const role = userRole || "Employee";

  // Tab configurations tailored by role
  const getTabs = () => {
    switch (role) {
      case "Admin":
        return [
          { key: "dashboard", label: "Dashboard", icon: Home },
          {
            key: "leaves",
            label: "Leaves",
            icon: ClipboardList,
            badge: pendingBadgeCount > 0 ? pendingBadgeCount : null,
          },
          { key: "employees", label: "Employees", icon: Users },
          { key: "timesheets", label: "Timesheets", icon: Clock3 },
          { key: "more", label: "More", icon: Menu, isAction: true },
        ];

      case "Manager":
        return [
          { key: "dashboard", label: "Dashboard", icon: Home },
          {
            key: "leaves",
            label: "Leaves",
            icon: ClipboardList,
            badge: pendingBadgeCount > 0 ? pendingBadgeCount : null,
          },
          { key: "employees", label: "My Team", icon: Users },
          { key: "timesheets", label: "Timesheets", icon: Clock3 },
          { key: "more", label: "More", icon: Menu, isAction: true },
        ];

      case "Employee":
      default:
        return [
          { key: "dashboard", label: "Home", icon: Home },
          { key: "leaves", label: "Leaves", icon: ClipboardList },
          { key: "timesheets", label: "Timesheets", icon: Clock3 },
          { key: "payslips", label: "Payslips", icon: FileBadge2 },
          { key: "more", label: "More", icon: Menu, isAction: true },
        ];
    }
  };

  const tabs = getTabs();

  const handleTabClick = (tab) => {
    if (tab.isAction) {
      onOpenDrawer?.();
      return;
    }
    onNavigate?.(tab.key);
  };

  return (
    <nav className="mobile-bottom-nav" aria-label="Mobile application navigation">
      <div className="mobile-bottom-nav-inner">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = !tab.isAction && currentSection === tab.key;

          return (
            <button
              key={tab.key}
              type="button"
              className={`mobile-bottom-nav-item ${isActive ? "is-active" : ""}`}
              onClick={() => handleTabClick(tab)}
              aria-current={isActive ? "page" : undefined}
              aria-label={tab.label}
            >
              <div className="mobile-bottom-nav-icon-wrapper">
                <Icon size={20} strokeWidth={isActive ? 2.3 : 1.9} />
                {Boolean(tab.badge) && (
                  <span className="mobile-bottom-nav-badge">
                    {tab.badge > 99 ? "99+" : tab.badge}
                  </span>
                )}
              </div>
              <span className="mobile-bottom-nav-label">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
